import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { setSizeError } from '../../redux/slices/shoppingCartSlice';
import { getCartValidationIssues } from '../../utils/cartValidation';
import {
  useCartMaterialPricingFetch,
  useCartTotalAndTaxEstimate,
  useCartValidationCleanup,
  useFetchUserCart,
} from './useShoppingCartPageEffects';

export const useShoppingCartPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isRootCartRoute = Boolean(useMatch('/cart'));
  const cartCheckoutMatch = useMatch('/cart/checkout');
  const cartCheckoutActive = location.pathname.startsWith('/cart/checkout');
  const cartItems = useAppSelector((state) => state.shoppingCart.cartItems);
  const materialPriceAndImgForCart = useAppSelector((state) => state.shoppingCart.materialPriceAndImgForCart);
  const loadingMaterialById = useAppSelector((state) => state.shoppingCart.loadingMaterialById);
  const totalAmount = useAppSelector((state) => state.shoppingCart.totalAmount);
  const status = useAppSelector((state) => state.shoppingCart.status);
  const error = useAppSelector((state) => state.shoppingCart.error);
  const taxEstimateAddress = useAppSelector((state) => state.shoppingCart.taxEstimateAddress);
  const authChecked = useAppSelector((state) => state.user.authChecked);
  const userShipping = useAppSelector((state) => state.user.shipping);
  const guestShipping = useAppSelector((state) => state.shoppingCart.guestShippingAddress);
  const userId = useAppSelector((state) => state.user.user?.id);
  const selectedMaterialsRef = useRef<Record<string, string>>({});
  const [animateOut, setAnimateOut] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [sizeErrors, setSizeErrors] = useState<string[]>([]);
  const isCartEmpty = cartItems.length === 0;
  const isCartHydrating =
    !authChecked ||
    ((status === 'loading' || status === 'idle') && cartItems.length === 0);
  const checkoutShipping = userId ? userShipping : guestShipping;
  const taxProvince = taxEstimateAddress.province || (cartCheckoutActive ? checkoutShipping?.province || '' : '');
  const materialDataByItemId = useMemo(
    () =>
      Object.fromEntries(
        materialPriceAndImgForCart.map((entry) => [entry.itemId, entry])
      ),
    [materialPriceAndImgForCart]
  );
  const cartValidationIssues = useMemo(
    () => getCartValidationIssues(cartItems, materialDataByItemId),
    [cartItems, materialDataByItemId]
  );
  const firstCartIssue = cartValidationIssues[0];
  const isCheckoutBlocked = isCartEmpty || totalAmount <= 0 || cartValidationIssues.length > 0;
  const checkoutDisabledMessage = isCartEmpty || totalAmount <= 0
    ? 'Checkout is unavailable until the cart total is above $0.00.'
    : firstCartIssue?.message;
  const isPricingLoading = useMemo(
    () => cartItems.some((item) => {
      const materialData = materialDataByItemId[item.id];
      return (
        Boolean(loadingMaterialById[item.id]) ||
        !materialData ||
        materialData.selectedMaterial !== item.selectedMaterial
      );
    }),
    [cartItems, loadingMaterialById, materialDataByItemId]
  );
  const animateClass = animateOut ? 'animate-slide-out' : animateIn ? 'animate-slide-in' : '';

  useCartValidationCleanup({ cartItems, cartValidationIssues, userId });
  useCartTotalAndTaxEstimate({
    cartItems,
    materialPriceAndImgForCart,
    province: taxProvince,
    totalAmount,
  });
  useFetchUserCart(userId);

  useEffect(() => {
    if (cartCheckoutMatch) {
      setAnimateOut(true);
      setAnimateIn(false);
    } else {
      setAnimateOut(false);
      setAnimateIn(true);
    }
  }, [cartCheckoutMatch]);

  useCartMaterialPricingFetch({ cartItems, selectedMaterialsRef });

  const handleCheckoutCart = useCallback(() => {
    if (isCartEmpty || totalAmount <= 0) {
      message.warning('Please add a product to the cart before checkout.');
      return;
    }

    const newSizeErrors: string[] = [];

    cartValidationIssues.forEach((issue) => {
      newSizeErrors.push(issue.itemId);
      dispatch(setSizeError({ itemId: issue.itemId, error: issue.message }));
    });

    setSizeErrors(newSizeErrors);

    if (newSizeErrors.length === 0) {
      setAnimateOut(true);
      window.setTimeout(() => {
        navigate('/cart/checkout');
        setAnimateOut(false);
        setAnimateIn(true);
      }, 300);
      return;
    }

    message.warning(firstCartIssue?.message || 'Please fix cart item availability before checkout.');
  }, [cartValidationIssues, dispatch, firstCartIssue?.message, isCartEmpty, navigate, totalAmount]);

  return {
    animateClass,
    cartItems,
    checkoutDisabledMessage,
    error,
    firstCartIssue,
    handleCheckoutCart,
    isCartEmpty,
    isCartHydrating,
    isCheckoutBlocked,
    isPricingLoading,
    isRootCartRoute,
    sizeErrors,
    status,
    totalAmount,
  };
};
